import { useEffect, useState } from 'react'
import { getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import { Button, Col, notification, Popconfirm, Row, Table, Tooltip } from 'antd'
import { useSearchParams } from 'react-router-dom'
import { Can, Icon, IconContainer, PageContainer, PageTitleRow } from '../../components'
import { handleBackendError } from '../../utils'
import ReverseDcModal from './ReverseDCModal'

import { createReverseDC, deleteReverseDC, getReverseDCList, updateReverseDC } from './api'

const getReverseDCs = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalVisible, setModalVisibility] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [isModalLoading, setModalLoading] = useState(false)
  const [reverseDCs, setReverseDCs] = useState([])
  const [selectedReverseDC, setSelectedReverseDC] = useState()
  const [errorMessage, setErrorMessage] = useState()

  useEffect(() => {
    fetchReverseDCsList(searchParams.toString())
  }, [searchParams])

  const fetchReverseDCsList = (params) => {
    setLoading(true)

    getReverseDCList(params)
      .then(({ data }) => {
        setReverseDCs(data.data)
        setLoading(false)
      })
      .catch((error) => {
        setLoading(false)

        handleFailure(error, 'Fetching dc list failed!')
      })
  }

  const tableColumns = [
    {
      className: 'reverseDCsNameSelenium',
      dataIndex: 'name',
      title: 'Name',
      ...getTableFilter({ name: 'name_cont' }),
    },
    {
      className: 'reverseDCsDetailsCopySelenium',
      dataIndex: 'details',
      key: 'details',
      title: 'Details',
    },
    {
      className: 'actionsSelenium',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <IconContainer>
          <Can
            no={() => (
              <Tooltip title="View">
                <Icon
                  data-testid="viewBtn"
                  name="updateReverseDCSelenium"
                  onClick={() => handleOpenModal(record)}
                  type="eye"
                />
              </Tooltip>
            )}
            requiredPermission={record.store_count === 0 ? 'reverse_dc.put.reverse_dc_id' : 'onlyView'}
            yes={() => (
              <Tooltip title="Edit">
                <Icon
                  data-testid="editBtn"
                  name="updateReverseDCSelenium"
                  onClick={() => handleOpenModal(record)}
                  type="edit"
                />
              </Tooltip>
            )}
          />
          {record.store_count === 0 && (
            <Can
              requiredPermission="reverse_dc.delete.reverse_dc_id"
              yes={() => (
                <Tooltip title="Delete">
                  <Popconfirm
                    cancelText="No"
                    className="action-list-button"
                    okText="Yes"
                    onConfirm={() => handleDeleteReverseDC(record)}
                    title="Are you sure you want to delete this reverse dc?"
                  >
                    <Icon data-testid="deleteIcon" name="deleteReverseDcSelenium" type="delete" />
                  </Popconfirm>
                </Tooltip>
              )}
            />
          )}
        </IconContainer>
      ),
      title: 'Actions',
    },
  ]

  const handleOpenModal = (selectedReverseDC) => {
    setSelectedReverseDC(selectedReverseDC)
    setErrorMessage(null)
    setModalVisibility(true)
    setModalLoading(false)
  }

  const handleSuccess = (messageOptions) => {
    fetchReverseDCsList(searchParams.toString())
    setErrorMessage(null)
    setModalVisibility(false)

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)

    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  const handleDeleteReverseDC = (record) => {
    deleteReverseDC(record.id)
      .then(() => handleSuccess({ description: 'Reverse dc removed!' }, true))
      .catch((error) => handleFailure(error, 'Reverse dc removal failed!'))
  }

  const handleFormSubmit = (actionType, formData) => {
    if (actionType === 'UPDATE') {
      updateReverseDC(formData)
        .then(() => handleSuccess({ description: 'Reverse dc updated!' }))
        .catch((error) => handleFailure(error, 'Reverse dc update failed!'))
    }

    if (actionType === 'CREATE') {
      createReverseDC(formData)
        .then(() => handleSuccess({ description: 'Reverse dc created!' }))
        .catch((error) => handleFailure(error, 'Reverse dc creation failed!'))
    }
  }

  return (
    <PageContainer>
      <PageTitleRow title="Reverse DCs">
        <Can
          requiredPermission="reverse_dc.post.create"
          yes={() => (
            <Button
              data-testid="newReverseDc"
              name="newReverseDcSelenium"
              onClick={() => handleOpenModal(null)}
              type="primary"
            >
              NEW REVERSE DC
            </Button>
          )}
        />
      </PageTitleRow>
      <Row>
        <Col span={24}>
          <Table
            className="reverseDCsPageSelenium"
            columns={tableColumns}
            data-testid="reverseDCSTable"
            dataSource={reverseDCs}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={false}
            rowKey="id"
          />

          {isModalVisible && (
            <ReverseDcModal
              errorMessage={errorMessage}
              hideModal={() => setModalVisibility(false)}
              isModalLoading={isModalLoading}
              onDelete={handleDeleteReverseDC}
              onSubmit={handleFormSubmit}
              selectedReverseDC={selectedReverseDC}
              setModalLoading={setModalLoading}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

export default getReverseDCs
